/**
 * AI Studio — Agent Factory API client (S1–S7).
 *
 * Targets `/api/v1/ai-studio`. The backend returns snake_case persistence rows in a
 * `{ success, data }` envelope; this module is the single boundary that maps them to
 * the camelCase view models in `@/types/aiStudioAgent`.
 */

import api from './api';
import type {
  AuthoringAgentPatch,
  AuthoringMessage,
  AuthoringSendResult,
  AuthoringSession,
  AuthoringVersionPatch,
  DefinitionProgress,
  AiStudioAgent,
  AiStudioAgentBinding,
  AiStudioAgentInstance,
  AiStudioAgentVersion,
  AiStudioDecisionCase,
  AiStudioDecisionContribution,
  AiStudioIndustryPack,
  AiStudioPromptBinding,
  AiStudioRun,
  AiStudioVersionComputeSlot,
  AiStudioVersionTool,
  AiOntologyAttribute,
  AiOntologyConcept,
  AiOntologyRelationship,
  CreateAgentRequest,
  CreateAgentResult,
  CreateBindingRequest,
  CreateInstanceRequest,
  CreateComputeSlotRequest,
  CreateVersionToolRequest,
  CreateDecisionCaseRequest,
  CreateDecisionContributionRequest,
  CreatePackRequest,
  CreateRunRequest,
  RunFromAgentResponse,
  RunReadinessResponse,
  ExpectedFieldRow,
  SetupCollectAt,
  InstanceDeployPatch,
  InstanceProvisionPatch,
  OntologyConceptKind,
  OntologyLifecycleStatus,
  ResourceTypeSeed,
  StudioAgentVisibility,
  StudioBindingStatus,
  StudioComputeClass,
  StudioDecisionAction,
  StudioDecisionCaseStatus,
  StudioDecisionMode,
  StudioExecutionMode,
  StudioLifecycleStatus,
  StudioAgentLifecycleStatus,
  StudioRunStatus,
  StudioSlotFallbackPolicy,
  StudioSpineStage,
  StudioToolKind,
  UpdateBindingRequest,
  UpdateInstanceRequest,
  UpdateComputeSlotRequest,
  UpdateVersionToolRequest,
  UpdateDecisionCaseRequest,
  UpdatePackRequest,
  PackAuthoringPatch,
  UpdateVersionRequest,
} from '@/types/aiStudioAgent';

const STUDIO_BASE = '/ai-studio';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

interface AgentRow {
  id: string;
  tenant_id: string | null;
  tenant_name?: string | null;
  code: string;
  name: string;
  description: string | null;
  domain: string | null;
  industry_tags: string[] | null;
  is_platform: boolean;
  purpose: string | null;
  scope_honesty: string | null;
  expert_posture: string | null;
  visibility: StudioAgentVisibility;
  lifecycle_status: StudioLifecycleStatus;
  created_by?: string | null;
  created_by_name?: string | null;
  created_at: string;
  updated_at: string;
}

interface AgentVersionRow {
  id: string;
  agent_id: string;
  version_number: number;
  enabled_stages: StudioSpineStage[] | null;
  data_schema: Record<string, unknown> | null;
  output_schema: Record<string, unknown> | null;
  benchmark_config: Record<string, unknown> | null;
  opportunity_logic: Record<string, unknown> | null;
  engine_config: Record<string, unknown> | null;
  promotion_rules: Record<string, unknown> | null;
  execution_mode: StudioExecutionMode;
  autonomy_fences: Record<string, unknown> | null;
  default_decision_mode?: StudioDecisionMode | null;
  context_contract: Record<string, unknown> | null;
  compute_classes: StudioComputeClass[] | null;
  archetype: string | null;
  industry_pack_id: string | null;
  input_expected: ExpectedFieldRow[] | null;
  output_expected: ExpectedFieldRow[] | null;
  context_definition: string | null;
  explain_instructions: string | null;
  success_criteria: string | null;
  hitl_required: boolean | null;
  lifecycle_status: StudioLifecycleStatus;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

interface PromptBindingRow {
  id: string;
  agent_version_id: string;
  prompt_template_id: string | null;
  prompt_role: string;
  task_type: string;
  variable_bindings: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

interface IndustryPackRow {
  id: string;
  tenant_id: string | null;
  code: string;
  name: string;
  domain: string | null;
  description: string | null;
  purpose: string | null;
  scope_honesty: string | null;
  expert_posture: string | null;
  vocabulary_map?: Record<string, unknown> | null;
  enabled_plugins?: unknown[] | null;
  default_weights?: Record<string, unknown> | null;
  import_template?: Record<string, unknown> | null;
  append_input_expected?: ExpectedFieldRow[] | null;
  append_output_expected?: ExpectedFieldRow[] | null;
  append_context?: string | null;
  append_instructions?: string | null;
  resource_types?: ResourceTypeSeed[] | null;
  lifecycle_status: StudioLifecycleStatus;
  created_at: string;
  updated_at: string;
}

interface ComputeSlotRow {
  id: string;
  tenant_id: string;
  agent_version_id: string;
  slot_name: string;
  compute_class: StudioComputeClass;
  default_ref: string | null;
  parameters: Record<string, unknown> | null;
  fallback_policy: StudioSlotFallbackPolicy;
  is_required: boolean;
  sort_order: number;
  description: string | null;
  created_at: string;
  updated_at: string;
}

function mapExpectedRows(rows: ExpectedFieldRow[] | null | undefined): ExpectedFieldRow[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    // Legacy rows may still carry source_hint / comments — fold into description.
    const raw = row as ExpectedFieldRow & {
      source_hint?: string;
      sourceHint?: string;
      comments?: string;
    };
    let description = (raw.description ?? '').trim();
    const legacySource = (raw.sourceHint ?? raw.source_hint ?? '').trim();
    const legacyComments = (raw.comments ?? '').trim();
    if (legacySource && !description.toLowerCase().includes(legacySource.toLowerCase())) {
      description = description
        ? `${description}\nSource: ${legacySource}`
        : `Source: ${legacySource}`;
    }
    if (
      legacyComments &&
      !description.toLowerCase().includes(legacyComments.toLowerCase())
    ) {
      description = description ? `${description}\n${legacyComments}` : legacyComments;
    }
    return {
      label: raw.label ?? '',
      description,
      required: raw.required ?? false,
      key: raw.key,
      type: raw.type as ExpectedFieldRow['type'],
      collectAt: raw.collectAt ?? (raw as { collect_at?: SetupCollectAt }).collect_at,
      enumValues: raw.enumValues ?? (raw as { enum_values?: string[] }).enum_values,
    };
  });
}

function mapResourceTypes(rows: ResourceTypeSeed[] | null | undefined): ResourceTypeSeed[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => ({
    typeCode: row.typeCode ?? (row as { type_code?: string }).type_code ?? '',
    displayName: row.displayName ?? (row as { display_name?: string }).display_name ?? '',
    attributes: Array.isArray(row.attributes)
      ? row.attributes.map((attr) => ({
          attributeCode:
            attr.attributeCode ?? (attr as { attribute_code?: string }).attribute_code ?? '',
          dataType: attr.dataType ?? (attr as { data_type?: string }).data_type ?? 'string',
          isRequired: attr.isRequired ?? (attr as { is_required?: boolean }).is_required ?? false,
        }))
      : [],
  }));
}

function mapComputeSlot(row: ComputeSlotRow): AiStudioVersionComputeSlot {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    agentVersionId: row.agent_version_id,
    slotName: row.slot_name,
    computeClass: row.compute_class,
    defaultRef: row.default_ref,
    parameters: row.parameters ?? {},
    fallbackPolicy: row.fallback_policy,
    isRequired: row.is_required,
    sortOrder: row.sort_order,
    description: row.description,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapAgent(row: AgentRow): AiStudioAgent {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    tenantName: row.tenant_name ?? null,
    code: row.code,
    name: row.name,
    description: row.description,
    domain: row.domain,
    industryTags: row.industry_tags ?? [],
    isPlatform: row.is_platform,
    purpose: row.purpose,
    scopeHonesty: row.scope_honesty,
    expertPosture: row.expert_posture,
    visibility: row.visibility ?? 'draft',
    lifecycleStatus: row.lifecycle_status as StudioAgentLifecycleStatus,
    createdBy: row.created_by ?? null,
    createdByName: row.created_by_name ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapVersion(row: AgentVersionRow): AiStudioAgentVersion {
  return {
    id: row.id,
    agentId: row.agent_id,
    versionNumber: row.version_number,
    enabledStages: row.enabled_stages ?? [],
    dataSchema: row.data_schema ?? {},
    outputSchema: row.output_schema ?? {},
    benchmarkConfig: row.benchmark_config ?? {},
    opportunityLogic: row.opportunity_logic ?? {},
    engineConfig: row.engine_config ?? {},
    promotionRules: row.promotion_rules ?? {},
    executionMode: row.execution_mode,
    autonomyFences: row.autonomy_fences ?? {},
    defaultDecisionMode:
      row.default_decision_mode === 'VERIFIED' || row.default_decision_mode === 'EVIDENCE_ONLY'
        ? row.default_decision_mode
        : 'PROVISIONAL',
    contextContract: row.context_contract ?? {},
    computeClasses: row.compute_classes ?? [],
    archetype: row.archetype,
    industryPackId: row.industry_pack_id,
    inputExpected: mapExpectedRows(row.input_expected),
    outputExpected: mapExpectedRows(row.output_expected),
    contextDefinition: row.context_definition,
    explainInstructions: row.explain_instructions,
    successCriteria: row.success_criteria,
    hitlRequired: row.hitl_required ?? true,
    lifecycleStatus: row.lifecycle_status,
    publishedAt: row.published_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapPromptBinding(row: PromptBindingRow): AiStudioPromptBinding {
  return {
    id: row.id,
    agentVersionId: row.agent_version_id,
    promptTemplateId: row.prompt_template_id,
    promptRole: row.prompt_role,
    taskType: row.task_type,
    variableBindings: row.variable_bindings ?? {},
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapPack(row: IndustryPackRow): AiStudioIndustryPack {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    code: row.code,
    name: row.name,
    domain: row.domain,
    description: row.description ?? null,
    purpose: row.purpose ?? null,
    scopeHonesty: row.scope_honesty ?? null,
    expertPosture: row.expert_posture ?? null,
    vocabularyMap: row.vocabulary_map ?? {},
    enabledPlugins: row.enabled_plugins ?? [],
    defaultWeights: row.default_weights ?? {},
    importTemplate: row.import_template ?? {},
    appendInputExpected: mapExpectedRows(row.append_input_expected),
    appendOutputExpected: mapExpectedRows(row.append_output_expected),
    appendContext: row.append_context ?? null,
    appendInstructions: row.append_instructions ?? null,
    resourceTypes: mapResourceTypes(row.resource_types),
    lifecycleStatus: row.lifecycle_status,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export async function listAgents(filter?: {
  status?: StudioLifecycleStatus;
  /** factory = authoring; catalog = bindable (Private tenant / Public all). */
  audience?: 'factory' | 'catalog';
}): Promise<AiStudioAgent[]> {
  const params: Record<string, string> = {};
  if (filter?.status) params.status = filter.status;
  if (filter?.audience) {
    params.audience = filter.audience;
  } else if (filter?.status === 'published') {
    // Binding pickers default to customer catalog ACL.
    params.audience = 'catalog';
  } else {
    params.audience = 'factory';
  }
  const response = await api.get<ApiEnvelope<AgentRow[]>>(`${STUDIO_BASE}/agents`, {
    params,
  });
  return response.data.data.map(mapAgent);
}

export async function getAgent(agentId: string): Promise<AiStudioAgent> {
  const response = await api.get<ApiEnvelope<AgentRow>>(`${STUDIO_BASE}/agents/${agentId}`);
  return mapAgent(response.data.data);
}

export async function createAgent(request: CreateAgentRequest): Promise<CreateAgentResult> {
  const body: Record<string, unknown> = {
    name: request.name,
    description: request.description ?? null,
    domain: request.domain ?? null,
    industry_tags: request.industryTags ?? [],
    is_platform: request.isPlatform ?? true,
    purpose: request.purpose ?? null,
    scope_honesty: request.scopeHonesty ?? null,
    expert_posture: request.expertPosture ?? null,
    visibility: request.visibility ?? 'draft',
    create_initial_version: request.createInitialVersion ?? true,
  };
  if (request.code) body['code'] = request.code;

  const response = await api.post<
    ApiEnvelope<{ agent: AgentRow; version: AgentVersionRow | null }>
  >(`${STUDIO_BASE}/agents`, body);

  return {
    agent: mapAgent(response.data.data.agent),
    version: response.data.data.version ? mapVersion(response.data.data.version) : null,
  };
}

/** Catalog Copy — always returns a new Draft agent cloned from the source. */
export async function copyAgent(agentId: string): Promise<CreateAgentResult> {
  const response = await api.post<
    ApiEnvelope<{ agent: AgentRow; version: AgentVersionRow | null }>
  >(`${STUDIO_BASE}/agents/${agentId}/copy`);

  return {
    agent: mapAgent(response.data.data.agent),
    version: response.data.data.version ? mapVersion(response.data.data.version) : null,
  };
}

export async function updateAgent(
  agentId: string,
  patch: {
    name?: string;
    code?: string;
    description?: string | null;
    domain?: string | null;
    industryTags?: string[];
    purpose?: string | null;
    scopeHonesty?: string | null;
    expertPosture?: string | null;
    visibility?: StudioAgentVisibility;
    lifecycleStatus?: StudioLifecycleStatus;
  }
): Promise<AiStudioAgent> {
  const body: Record<string, unknown> = {};
  if (patch.name !== undefined) body['name'] = patch.name;
  if (patch.code !== undefined) body['code'] = patch.code;
  if (patch.description !== undefined) body['description'] = patch.description;
  if (patch.domain !== undefined) body['domain'] = patch.domain;
  if (patch.industryTags !== undefined) body['industry_tags'] = patch.industryTags;
  if (patch.purpose !== undefined) body['purpose'] = patch.purpose;
  if (patch.scopeHonesty !== undefined) body['scope_honesty'] = patch.scopeHonesty;
  if (patch.expertPosture !== undefined) body['expert_posture'] = patch.expertPosture;
  if (patch.visibility !== undefined) body['visibility'] = patch.visibility;
  if (patch.lifecycleStatus !== undefined) body['lifecycle_status'] = patch.lifecycleStatus;

  const response = await api.patch<ApiEnvelope<AgentRow>>(`${STUDIO_BASE}/agents/${agentId}`, body);
  return mapAgent(response.data.data);
}

/** Hard-delete a draft agent (not archive). */
export async function deleteAgent(agentId: string): Promise<void> {
  await api.delete(`${STUDIO_BASE}/agents/${agentId}`);
}

export interface AgentSetupDocument {
  setupText: string;
  version: number;
  hasCommittedPlan: boolean;
  aclCount: number;
  unresolvedHint: string | null;
  configured: boolean;
}

export async function getAgentSetup(agentId: string): Promise<AgentSetupDocument> {
  const response = await api.get<
    ApiEnvelope<{
      setupText?: string;
      setup_text?: string;
      version: number;
      hasCommittedPlan?: boolean;
      has_committed_plan?: boolean;
      aclCount?: number;
      acl_count?: number;
      unresolvedHint?: string | null;
      unresolved_hint?: string | null;
      configured?: boolean;
    }>
  >(`${STUDIO_BASE}/agents/${agentId}/setup`);
  const row = response.data.data;
  return {
    setupText: row.setupText ?? row.setup_text ?? '',
    version: Number(row.version),
    hasCommittedPlan: Boolean(row.hasCommittedPlan ?? row.has_committed_plan),
    aclCount: Number(row.aclCount ?? row.acl_count ?? 0),
    unresolvedHint: row.unresolvedHint ?? row.unresolved_hint ?? null,
    configured: Boolean(row.configured),
  };
}

export async function putAgentSetup(
  agentId: string,
  setupText: string
): Promise<AgentSetupDocument> {
  const response = await api.put<
    ApiEnvelope<{
      setupText?: string;
      setup_text?: string;
      version: number;
      aclCount?: number;
      acl_count?: number;
      hasCommittedPlan?: boolean;
      has_committed_plan?: boolean;
    }>
  >(`${STUDIO_BASE}/agents/${agentId}/setup`, { setupText });
  const row = response.data.data;
  return {
    setupText: row.setupText ?? row.setup_text ?? setupText,
    version: Number(row.version),
    hasCommittedPlan: Boolean(row.hasCommittedPlan ?? row.has_committed_plan),
    aclCount: Number(row.aclCount ?? row.acl_count ?? 0),
    unresolvedHint: null,
    configured: true,
  };
}

export async function listVersions(agentId: string): Promise<AiStudioAgentVersion[]> {
  const response = await api.get<ApiEnvelope<AgentVersionRow[]>>(
    `${STUDIO_BASE}/agents/${agentId}/versions`
  );
  return response.data.data.map(mapVersion);
}

export async function getVersion(versionId: string): Promise<AiStudioAgentVersion> {
  const response = await api.get<ApiEnvelope<AgentVersionRow>>(
    `${STUDIO_BASE}/versions/${versionId}`
  );
  return mapVersion(response.data.data);
}

/** Creates the next draft version. The backend assigns the version number. */
export async function createVersion(agentId: string): Promise<AiStudioAgentVersion> {
  const response = await api.post<ApiEnvelope<AgentVersionRow>>(
    `${STUDIO_BASE}/agents/${agentId}/versions`,
    {}
  );
  return mapVersion(response.data.data);
}

export async function updateVersion(
  versionId: string,
  patch: UpdateVersionRequest
): Promise<AiStudioAgentVersion> {
  const body: Record<string, unknown> = {};
  if (patch.enabledStages !== undefined) body['enabled_stages'] = patch.enabledStages;
  if (patch.dataSchema !== undefined) body['data_schema'] = patch.dataSchema;
  if (patch.outputSchema !== undefined) body['output_schema'] = patch.outputSchema;
  if (patch.benchmarkConfig !== undefined) body['benchmark_config'] = patch.benchmarkConfig;
  if (patch.opportunityLogic !== undefined) body['opportunity_logic'] = patch.opportunityLogic;
  if (patch.engineConfig !== undefined) body['engine_config'] = patch.engineConfig;
  if (patch.promotionRules !== undefined) body['promotion_rules'] = patch.promotionRules;
  if (patch.executionMode !== undefined) body['execution_mode'] = patch.executionMode;
  if (patch.autonomyFences !== undefined) body['autonomy_fences'] = patch.autonomyFences;
  if (patch.defaultDecisionMode !== undefined) {
    body['default_decision_mode'] = patch.defaultDecisionMode;
  }
  if (patch.contextContract !== undefined) body['context_contract'] = patch.contextContract;
  if (patch.computeClasses !== undefined) body['compute_classes'] = patch.computeClasses;
  if (patch.archetype !== undefined) body['archetype'] = patch.archetype;
  if (patch.industryPackId !== undefined) body['industry_pack_id'] = patch.industryPackId;
  if (patch.inputExpected !== undefined) body['input_expected'] = patch.inputExpected;
  if (patch.outputExpected !== undefined) body['output_expected'] = patch.outputExpected;
  if (patch.contextDefinition !== undefined) body['context_definition'] = patch.contextDefinition;
  if (patch.explainInstructions !== undefined) body['explain_instructions'] = patch.explainInstructions;
  if (patch.successCriteria !== undefined) body['success_criteria'] = patch.successCriteria;
  if (patch.hitlRequired !== undefined) body['hitl_required'] = patch.hitlRequired;

  const response = await api.patch<ApiEnvelope<AgentVersionRow>>(
    `${STUDIO_BASE}/versions/${versionId}`,
    body
  );
  return mapVersion(response.data.data);
}

export async function publishVersion(versionId: string): Promise<AiStudioAgentVersion> {
  const response = await api.post<ApiEnvelope<AgentVersionRow>>(
    `${STUDIO_BASE}/versions/${versionId}/publish`
  );
  return mapVersion(response.data.data);
}

export async function listComputeSlots(
  versionId: string,
  computeClass?: StudioComputeClass
): Promise<AiStudioVersionComputeSlot[]> {
  const response = await api.get<ApiEnvelope<ComputeSlotRow[]>>(
    `${STUDIO_BASE}/versions/${versionId}/compute-slots`,
    computeClass ? { params: { computeClass } } : undefined
  );
  return response.data.data.map(mapComputeSlot);
}

export async function createComputeSlot(
  versionId: string,
  request: CreateComputeSlotRequest
): Promise<AiStudioVersionComputeSlot> {
  const response = await api.post<ApiEnvelope<ComputeSlotRow>>(
    `${STUDIO_BASE}/versions/${versionId}/compute-slots`,
    {
      slot_name: request.slotName,
      compute_class: request.computeClass,
      default_ref: request.defaultRef ?? null,
      parameters: request.parameters ?? {},
      fallback_policy: request.fallbackPolicy ?? 'error',
      is_required: request.isRequired ?? true,
      sort_order: request.sortOrder ?? 0,
      description: request.description ?? null,
    }
  );
  return mapComputeSlot(response.data.data);
}

export async function updateComputeSlot(
  versionId: string,
  slotId: string,
  request: UpdateComputeSlotRequest
): Promise<AiStudioVersionComputeSlot> {
  const body: Record<string, unknown> = {};
  if (request.slotName !== undefined) body['slot_name'] = request.slotName;
  if (request.computeClass !== undefined) body['compute_class'] = request.computeClass;
  if (request.defaultRef !== undefined) body['default_ref'] = request.defaultRef;
  if (request.parameters !== undefined) body['parameters'] = request.parameters;
  if (request.fallbackPolicy !== undefined) body['fallback_policy'] = request.fallbackPolicy;
  if (request.isRequired !== undefined) body['is_required'] = request.isRequired;
  if (request.sortOrder !== undefined) body['sort_order'] = request.sortOrder;
  if (request.description !== undefined) body['description'] = request.description;

  const response = await api.patch<ApiEnvelope<ComputeSlotRow>>(
    `${STUDIO_BASE}/versions/${versionId}/compute-slots/${slotId}`,
    body
  );
  return mapComputeSlot(response.data.data);
}

export async function deleteComputeSlot(versionId: string, slotId: string): Promise<void> {
  await api.delete(`${STUDIO_BASE}/versions/${versionId}/compute-slots/${slotId}`);
}

interface VersionToolRow {
  id: string;
  tenant_id: string | null;
  agent_version_id: string;
  tool_name: string;
  tool_kind: StudioToolKind;
  handler_ref: string;
  description: string;
  parameters_schema?: Record<string, unknown> | null;
  is_enabled: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

function mapVersionTool(row: VersionToolRow): AiStudioVersionTool {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    agentVersionId: row.agent_version_id,
    toolName: row.tool_name,
    toolKind: row.tool_kind,
    handlerRef: row.handler_ref,
    description: row.description,
    parametersSchema: row.parameters_schema ?? {},
    isEnabled: Boolean(row.is_enabled),
    sortOrder: Number(row.sort_order) || 0,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export async function listVersionTools(versionId: string): Promise<AiStudioVersionTool[]> {
  const response = await api.get<ApiEnvelope<VersionToolRow[]>>(
    `${STUDIO_BASE}/versions/${versionId}/tools`
  );
  return response.data.data.map(mapVersionTool);
}

export async function createVersionTool(
  versionId: string,
  request: CreateVersionToolRequest
): Promise<AiStudioVersionTool> {
  const response = await api.post<ApiEnvelope<VersionToolRow>>(
    `${STUDIO_BASE}/versions/${versionId}/tools`,
    {
      tool_name: request.toolName,
      tool_kind: request.toolKind,
      handler_ref: request.handlerRef,
      description: request.description,
      parameters_schema: request.parametersSchema ?? {},
      is_enabled: request.isEnabled ?? true,
      sort_order: request.sortOrder ?? 0,
    }
  );
  return mapVersionTool(response.data.data);
}

export async function updateVersionTool(
  versionId: string,
  toolId: string,
  request: UpdateVersionToolRequest
): Promise<AiStudioVersionTool> {
  const body: Record<string, unknown> = {};
  if (request.toolName !== undefined) body['tool_name'] = request.toolName;
  if (request.toolKind !== undefined) body['tool_kind'] = request.toolKind;
  if (request.handlerRef !== undefined) body['handler_ref'] = request.handlerRef;
  if (request.description !== undefined) body['description'] = request.description;
  if (request.parametersSchema !== undefined) body['parameters_schema'] = request.parametersSchema;
  if (request.isEnabled !== undefined) body['is_enabled'] = request.isEnabled;
  if (request.sortOrder !== undefined) body['sort_order'] = request.sortOrder;
  const response = await api.patch<ApiEnvelope<VersionToolRow>>(
    `${STUDIO_BASE}/versions/${versionId}/tools/${toolId}`,
    body
  );
  return mapVersionTool(response.data.data);
}

export async function deleteVersionTool(versionId: string, toolId: string): Promise<void> {
  await api.delete(`${STUDIO_BASE}/versions/${versionId}/tools/${toolId}`);
}

export interface PlatformToolHandler {
  handlerRef: string;
  category: 'decision' | 'scheduling' | 'quality';
  defaultToolName: string;
  toolKind: StudioToolKind;
  description: string;
  parametersSchema: Record<string, unknown>;
}

export async function listPlatformToolHandlers(
  category?: string
): Promise<PlatformToolHandler[]> {
  const response = await api.get<
    ApiEnvelope<
      Array<{
        handler_ref: string;
        category: string;
        default_tool_name: string;
        tool_kind: string;
        description: string;
        parameters_schema: Record<string, unknown>;
      }>
    >
  >(`${STUDIO_BASE}/tool-handlers`, {
    params: category ? { category } : undefined,
  });
  return response.data.data.map((row) => ({
    handlerRef: row.handler_ref,
    category: row.category as PlatformToolHandler['category'],
    defaultToolName: row.default_tool_name,
    toolKind: row.tool_kind as StudioToolKind,
    description: row.description,
    parametersSchema: row.parameters_schema ?? {},
  }));
}

export async function seedCoreDecisionTools(versionId: string): Promise<AiStudioVersionTool[]> {
  const response = await api.post<ApiEnvelope<VersionToolRow[]>>(
    `${STUDIO_BASE}/versions/${versionId}/tools/seed-core-decision`
  );
  return response.data.data.map(mapVersionTool);
}

export async function listRunArtifacts(runId: string): Promise<
  Array<{
    id: string;
    title: string;
    summary?: string;
    kind: 'schedule' | 'document' | 'data';
    status?: 'draft' | 'saved';
    body: Record<string, unknown>;
    persistence?: 'episode' | 'instance';
    expiresAt?: string | null;
    stale?: boolean;
  }>
> {
  const response = await api.get<
    ApiEnvelope<
      Array<{
        id: string;
        title: string;
        summary?: string;
        kind: 'schedule' | 'document' | 'data';
        status?: 'draft' | 'saved';
        body: Record<string, unknown>;
        persistence?: 'episode' | 'instance';
        expiresAt?: string | null;
        stale?: boolean;
      }>
    >
  >(`${STUDIO_BASE}/runs/${runId}/artifacts`);
  return response.data.data;
}

/** Download an Outputs artifact as PDF or Word (same content as the document modal). */
export async function exportStudioOutput(
  format: 'pdf' | 'docx',
  exportDocument: {
    title: string;
    summary?: string;
    blocks: Array<
      | { type: 'paragraph'; text: string }
      | { type: 'table'; columns: string[]; rows: string[][] }
      | { type: 'note'; text: string }
    >;
  }
): Promise<void> {
  const { data } = await api.post<Blob>(
    `${STUDIO_BASE}/outputs/export`,
    { format, document: exportDocument },
    { responseType: 'blob', timeout: 60000 }
  );
  const blob = data instanceof Blob ? data : new Blob([data]);
  const safeTitle = exportDocument.title
    .trim()
    .replace(/[^\w\s-]+/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
  const filename = `${safeTitle || 'studio-output'}.${format}`;
  const anchor = window.document.createElement('a');
  anchor.href = URL.createObjectURL(blob);
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(anchor.href);
}

/** New conversation = new run + conversation on the same instance (1:1 episode). */
export async function startNewEpisode(request: {
  instanceId: string;
  runName?: string;
}): Promise<{ run: AiStudioRun; conversationId: string }> {
  const response = await api.post<
    ApiEnvelope<{
      run: RunRow;
      conversation: { id: string };
    }>
  >(`${STUDIO_BASE}/episodes`, {
    instance_id: request.instanceId,
    binding_id: request.instanceId,
    run_name: request.runName,
  });
  return {
    run: mapRun(response.data.data.run),
    conversationId: response.data.data.conversation.id,
  };
}

export async function listPromptBindings(versionId: string): Promise<AiStudioPromptBinding[]> {
  const response = await api.get<ApiEnvelope<PromptBindingRow[]>>(
    `${STUDIO_BASE}/versions/${versionId}/prompt-bindings`
  );
  return response.data.data.map(mapPromptBinding);
}

export async function createPromptBinding(
  versionId: string,
  request: { promptRole: string; taskType: string; promptTemplateId?: string | null }
): Promise<AiStudioPromptBinding> {
  const response = await api.post<ApiEnvelope<PromptBindingRow>>(
    `${STUDIO_BASE}/versions/${versionId}/prompt-bindings`,
    {
      prompt_role: request.promptRole,
      task_type: request.taskType,
      prompt_template_id: request.promptTemplateId ?? null,
    }
  );
  return mapPromptBinding(response.data.data);
}

export async function listIndustryPacks(): Promise<AiStudioIndustryPack[]> {
  const response = await api.get<ApiEnvelope<IndustryPackRow[]>>(`${STUDIO_BASE}/packs`);
  return response.data.data.map(mapPack);
}

/** Published packs linked to the agent's published versions (PLAT-1). */
export async function listCompatibleIndustryPacks(
  agentId: string
): Promise<AiStudioIndustryPack[]> {
  const response = await api.get<ApiEnvelope<IndustryPackRow[]>>(
    `${STUDIO_BASE}/agents/${agentId}/compatible-packs`
  );
  return response.data.data.map(mapPack);
}

export async function getIndustryPack(packId: string): Promise<AiStudioIndustryPack> {
  const response = await api.get<ApiEnvelope<IndustryPackRow>>(`${STUDIO_BASE}/packs/${packId}`);
  return mapPack(response.data.data);
}

export async function createIndustryPack(request: CreatePackRequest): Promise<AiStudioIndustryPack> {
  const body: Record<string, unknown> = {
    name: request.name,
    domain: request.domain ?? null,
    is_platform: request.isPlatform ?? false,
  };
  if (request.code) body['code'] = request.code;

  const response = await api.post<ApiEnvelope<IndustryPackRow>>(`${STUDIO_BASE}/packs`, body);
  return mapPack(response.data.data);
}

export async function updateIndustryPack(
  packId: string,
  request: UpdatePackRequest
): Promise<AiStudioIndustryPack> {
  const body: Record<string, unknown> = {};
  if (request.name !== undefined) body.name = request.name;
  if (request.domain !== undefined) body.domain = request.domain;
  if (request.description !== undefined) body.description = request.description;
  if (request.purpose !== undefined) body.purpose = request.purpose;
  if (request.scopeHonesty !== undefined) body.scope_honesty = request.scopeHonesty;
  if (request.expertPosture !== undefined) body.expert_posture = request.expertPosture;
  if (request.vocabularyMap !== undefined) body.vocabulary_map = request.vocabularyMap;
  if (request.enabledPlugins !== undefined) body.enabled_plugins = request.enabledPlugins;
  if (request.defaultWeights !== undefined) body.default_weights = request.defaultWeights;
  if (request.importTemplate !== undefined) body.import_template = request.importTemplate;
  if (request.appendInputExpected !== undefined) body.append_input_expected = request.appendInputExpected;
  if (request.appendOutputExpected !== undefined) body.append_output_expected = request.appendOutputExpected;
  if (request.appendContext !== undefined) body.append_context = request.appendContext;
  if (request.appendInstructions !== undefined) body.append_instructions = request.appendInstructions;
  if (request.resourceTypes !== undefined) body.resource_types = request.resourceTypes;

  const response = await api.patch<ApiEnvelope<IndustryPackRow>>(
    `${STUDIO_BASE}/packs/${packId}`,
    body
  );
  return mapPack(response.data.data);
}

export async function publishIndustryPack(packId: string): Promise<AiStudioIndustryPack> {
  const response = await api.post<ApiEnvelope<IndustryPackRow>>(
    `${STUDIO_BASE}/packs/${packId}/publish`
  );
  return mapPack(response.data.data);
}

interface BindingRow {
  id: string;
  tenant_id: string;
  agent_version_id: string;
  industry_pack_id: string | null;
  binding_name: string;
  customer_setup?: Record<string, unknown> | null;
  autonomy_fences_override?: Record<string, unknown> | null;
  tool_connections?: Record<string, unknown> | null;
  memory_scope?: Record<string, unknown> | null;
  status: StudioBindingStatus;
  created_at: string;
  updated_at: string;
  agent_code?: string;
  agent_name?: string;
  version_number?: number;
  pack_code?: string | null;
  pack_name?: string | null;
  execution_mode?: StudioExecutionMode;
  version_autonomy_fences?: Record<string, unknown> | null;
}

function mapBinding(row: BindingRow): AiStudioAgentBinding {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    agentVersionId: row.agent_version_id,
    industryPackId: row.industry_pack_id,
    bindingName: row.binding_name,
    customerSetup: row.customer_setup ?? {},
    autonomyFencesOverride: row.autonomy_fences_override ?? {},
    toolConnections: row.tool_connections ?? {},
    memoryScope: row.memory_scope ?? {},
    status: row.status,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    agentCode: row.agent_code,
    agentName: row.agent_name,
    versionNumber: row.version_number,
    packCode: row.pack_code ?? null,
    packName: row.pack_name ?? null,
    executionMode: row.execution_mode,
    versionAutonomyFences: row.version_autonomy_fences ?? {},
  };
}

export async function listBindings(): Promise<AiStudioAgentBinding[]> {
  const response = await api.get<ApiEnvelope<BindingRow[]>>(`${STUDIO_BASE}/bindings`);
  return response.data.data.map(mapBinding);
}

export async function getBinding(bindingId: string): Promise<AiStudioAgentBinding> {
  const response = await api.get<ApiEnvelope<BindingRow>>(`${STUDIO_BASE}/bindings/${bindingId}`);
  return mapBinding(response.data.data);
}

export async function createBinding(request: CreateBindingRequest): Promise<AiStudioAgentBinding> {
  const response = await api.post<ApiEnvelope<BindingRow>>(`${STUDIO_BASE}/bindings`, {
    agent_version_id: request.agentVersionId,
    binding_name: request.bindingName,
    industry_pack_id: request.industryPackId ?? null,
    status: request.status ?? 'active',
  });
  return mapBinding(response.data.data);
}

export async function updateBinding(
  bindingId: string,
  request: UpdateBindingRequest
): Promise<AiStudioAgentBinding> {
  const body: Record<string, unknown> = {};
  if (request.bindingName !== undefined) body.binding_name = request.bindingName;
  if (request.industryPackId !== undefined) body.industry_pack_id = request.industryPackId;
  if (request.customerSetup !== undefined) body.customer_setup = request.customerSetup;
  if (request.autonomyFencesOverride !== undefined) {
    body.autonomy_fences_override = request.autonomyFencesOverride;
  }
  if (request.toolConnections !== undefined) body.tool_connections = request.toolConnections;
  if (request.memoryScope !== undefined) body.memory_scope = request.memoryScope;
  if (request.status !== undefined) body.status = request.status;

  const response = await api.patch<ApiEnvelope<BindingRow>>(
    `${STUDIO_BASE}/bindings/${bindingId}`,
    body
  );
  return mapBinding(response.data.data);
}

/** Newer published versions than the binding production pin (REQ-AIS-015). */
export async function listBindingUpgrades(bindingId: string): Promise<AiStudioAgentVersion[]> {
  const response = await api.get<ApiEnvelope<AgentVersionRow[]>>(
    `${STUDIO_BASE}/bindings/${bindingId}/upgrades`
  );
  return response.data.data.map(mapVersion);
}

/** Explicit production pin switch after trial (REQ-AIS-015). */
export async function switchBindingVersion(
  bindingId: string,
  agentVersionId: string
): Promise<AiStudioAgentBinding> {
  const response = await api.post<ApiEnvelope<BindingRow>>(
    `${STUDIO_BASE}/bindings/${bindingId}/switch-version`,
    { agent_version_id: agentVersionId }
  );
  return mapBinding(response.data.data);
}

interface InstanceRow {
  id: string;
  tenant_id: string;
  pinned_version_id: string;
  industry_pack_id: string | null;
  instance_name: string;
  instance_setup?: Record<string, unknown> | null;
  autonomy_fences_override?: Record<string, unknown> | null;
  tool_connections?: Record<string, unknown> | null;
  memory_scope?: Record<string, unknown> | null;
  status: StudioBindingStatus;
  created_at: string;
  updated_at: string;
  agent_id?: string;
  agent_code?: string;
  agent_name?: string;
  version_number?: number;
  pack_code?: string | null;
  pack_name?: string | null;
  execution_mode?: StudioExecutionMode;
  version_autonomy_fences?: Record<string, unknown> | null;
  business_hierarchy_id?: string | null;
  functional_hierarchy_id?: string | null;
  geographic_hierarchy_id?: string | null;
  setup_configured?: boolean;
  setup_status?: string;
  requires_setup?: boolean;
}

function mapInstance(row: InstanceRow): AiStudioAgentInstance {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    pinnedVersionId: row.pinned_version_id,
    industryPackId: row.industry_pack_id,
    instanceName: row.instance_name,
    instanceSetup: row.instance_setup ?? {},
    autonomyFencesOverride: row.autonomy_fences_override ?? {},
    toolConnections: row.tool_connections ?? {},
    memoryScope: row.memory_scope ?? {},
    status: row.status,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    agentId: row.agent_id,
    agentCode: row.agent_code,
    agentName: row.agent_name,
    versionNumber: row.version_number,
    packCode: row.pack_code ?? null,
    packName: row.pack_name ?? null,
    executionMode: row.execution_mode,
    versionAutonomyFences: row.version_autonomy_fences ?? {},
    businessHierarchyId: row.business_hierarchy_id ?? null,
    functionalHierarchyId: row.functional_hierarchy_id ?? null,
    geographicHierarchyId: row.geographic_hierarchy_id ?? null,
    setupConfigured: row.setup_configured ?? false,
    setupStatus: row.setup_status,
    requiresSetup: row.requires_setup ?? false,
  };
}

export async function listInstances(): Promise<AiStudioAgentInstance[]> {
  const response = await api.get<ApiEnvelope<InstanceRow[]>>(`${STUDIO_BASE}/instances`);
  return response.data.data.map(mapInstance);
}

export async function getInstance(instanceId: string): Promise<AiStudioAgentInstance> {
  const response = await api.get<ApiEnvelope<InstanceRow>>(
    `${STUDIO_BASE}/instances/${instanceId}`
  );
  return mapInstance(response.data.data);
}

export async function createInstance(
  request: CreateInstanceRequest
): Promise<AiStudioAgentInstance> {
  const response = await api.post<ApiEnvelope<InstanceRow>>(`${STUDIO_BASE}/instances`, {
    pinned_version_id: request.pinnedVersionId,
    instance_name: request.instanceName,
    industry_pack_id: request.industryPackId ?? null,
    status: request.status ?? 'active',
    business_hierarchy_id: request.businessHierarchyId ?? null,
    functional_hierarchy_id: request.functionalHierarchyId ?? null,
    geographic_hierarchy_id: request.geographicHierarchyId ?? null,
  });
  return mapInstance(response.data.data);
}

export async function updateInstance(
  instanceId: string,
  request: UpdateInstanceRequest
): Promise<AiStudioAgentInstance> {
  const body: Record<string, unknown> = {};
  if (request.instanceName !== undefined) body.instance_name = request.instanceName;
  if (request.industryPackId !== undefined) body.industry_pack_id = request.industryPackId;
  if (request.instanceSetup !== undefined) body.instance_setup = request.instanceSetup;
  if (request.autonomyFencesOverride !== undefined) {
    body.autonomy_fences_override = request.autonomyFencesOverride;
  }
  if (request.toolConnections !== undefined) body.tool_connections = request.toolConnections;
  if (request.memoryScope !== undefined) body.memory_scope = request.memoryScope;
  if (request.status !== undefined) body.status = request.status;

  const response = await api.patch<ApiEnvelope<InstanceRow>>(
    `${STUDIO_BASE}/instances/${instanceId}`,
    body
  );
  return mapInstance(response.data.data);
}

export async function deleteInstance(instanceId: string): Promise<void> {
  await api.delete(`${STUDIO_BASE}/instances/${instanceId}`);
}

export async function listInstanceUpgrades(instanceId: string): Promise<AiStudioAgentVersion[]> {
  const response = await api.get<ApiEnvelope<AgentVersionRow[]>>(
    `${STUDIO_BASE}/instances/${instanceId}/upgrades`
  );
  return response.data.data.map(mapVersion);
}

export async function switchInstanceVersion(
  instanceId: string,
  pinnedVersionId: string
): Promise<AiStudioAgentInstance> {
  const response = await api.post<ApiEnvelope<InstanceRow>>(
    `${STUDIO_BASE}/instances/${instanceId}/switch-version`,
    { pinned_version_id: pinnedVersionId }
  );
  return mapInstance(response.data.data);
}

interface RunRow {
  id: string;
  tenant_id: string;
  agent_version_id: string;
  binding_id: string | null;
  owner_user_id: string | null;
  run_name: string;
  situation_type: string | null;
  effective_tenant_id: string | null;
  status: StudioRunStatus;
  scope_context?: Record<string, unknown> | null;
  current_thesis?: Record<string, unknown> | null;
  working_set?: Record<string, unknown> | null;
  compute_result?: Record<string, unknown> | null;
  is_trial?: boolean;
  created_at: string;
  updated_at: string;
  binding_name?: string;
  agent_id?: string;
  agent_code?: string;
  agent_name?: string;
  agent_purpose?: string | null;
  agent_expert_posture?: string | null;
  version_number?: number;
  execution_mode?: StudioExecutionMode;
  owner_name?: string | null;
  owner_email?: string | null;
}

function mapRun(row: RunRow): AiStudioRun {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    agentVersionId: row.agent_version_id,
    bindingId: row.binding_id,
    ownerUserId: row.owner_user_id,
    runName: row.run_name,
    situationType: row.situation_type,
    effectiveTenantId: row.effective_tenant_id,
    status: row.status,
    scopeContext: row.scope_context ?? {},
    currentThesis: row.current_thesis ?? {},
    workingSet: row.working_set ?? {},
    computeResult: row.compute_result ?? null,
    isTrial: Boolean(row.is_trial),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    bindingName: row.binding_name,
    agentId: row.agent_id,
    agentCode: row.agent_code,
    agentName: row.agent_name,
    agentPurpose: row.agent_purpose ?? null,
    agentExpertPosture: row.agent_expert_posture ?? null,
    versionNumber: row.version_number,
    executionMode: row.execution_mode,
    ownerName: row.owner_name ?? null,
    ownerEmail: row.owner_email ?? null,
  };
}

export async function listRuns(): Promise<AiStudioRun[]> {
  const response = await api.get<ApiEnvelope<RunRow[]>>(`${STUDIO_BASE}/runs`);
  return response.data.data.map(mapRun);
}

export async function getRun(runId: string): Promise<AiStudioRun> {
  const response = await api.get<ApiEnvelope<RunRow>>(`${STUDIO_BASE}/runs/${runId}`);
  return mapRun(response.data.data);
}

/** Factory Test: draft version run without publishing (Platform Admin). */
export async function startStudioTestRun(agentId: string): Promise<{
  run: AiStudioRun;
  conversationId: string | null;
  instanceId: string;
}> {
  const response = await api.post<
    ApiEnvelope<{
      run: RunRow;
      conversation: { id: string } | null;
      instanceId: string;
      versionId: string;
    }>
  >(`${STUDIO_BASE}/agents/${agentId}/test-run`, {});
  const payload = response.data.data;
  return {
    run: mapRun(payload.run),
    conversationId: payload.conversation?.id ?? null,
    instanceId: payload.instanceId,
  };
}

export async function createRun(request: CreateRunRequest): Promise<AiStudioRun> {
  const response = await api.post<ApiEnvelope<RunRow>>(`${STUDIO_BASE}/runs`, {
    binding_id: request.bindingId ?? request.instanceId,
    instance_id: request.instanceId ?? request.bindingId,
    run_name: request.runName,
    situation_type: request.situationType ?? null,
    scope_context: request.scopeContext ?? {},
    trial_agent_version_id: request.trialAgentVersionId ?? null,
  });
  return mapRun(response.data.data);
}

/** UX1: start a run from an agent (silent pin in caller's tenant). */
export async function createRunFromAgent(
  request: CreateRunRequest & { agentId: string }
): Promise<RunFromAgentResponse> {
  const response = await api.post<
    ApiEnvelope<{
      run: RunRow;
      readiness: { ready: boolean; messages: string[] };
      agentArchived: boolean;
    }>
  >(`${STUDIO_BASE}/runs`, {
    agent_id: request.agentId,
    run_name: request.runName,
    situation_type: request.situationType ?? null,
    scope_context: request.scopeContext ?? {},
    acknowledge_archived: request.acknowledgeArchived === true,
  });
  return {
    run: mapRun(response.data.data.run),
    readiness: response.data.data.readiness,
    agentArchived: response.data.data.agentArchived,
  };
}

export async function getRunReadiness(runId: string): Promise<RunReadinessResponse> {
  const response = await api.get<ApiEnvelope<RunReadinessResponse>>(
    `${STUDIO_BASE}/runs/${runId}/readiness`
  );
  return response.data.data;
}

export async function updateRunWorkingSet(
  runId: string,
  workingSet: Record<string, unknown>
): Promise<AiStudioRun> {
  const response = await api.put<ApiEnvelope<RunRow>>(
    `${STUDIO_BASE}/runs/${runId}/working-set`,
    { working_set: workingSet }
  );
  return mapRun(response.data.data);
}

export interface RunComputeResponse {
  run: AiStudioRun;
  computeResult: Record<string, unknown>;
  decisionCaseId: string | null;
}

export async function computeRun(
  runId: string,
  slotName?: string
): Promise<RunComputeResponse> {
  const response = await api.post<
    ApiEnvelope<{
      run: RunRow;
      computeResult: Record<string, unknown>;
      decisionCaseId: string | null;
    }>
  >(`${STUDIO_BASE}/runs/${runId}/compute`, slotName ? { slot_name: slotName } : {});
  return {
    run: mapRun(response.data.data.run),
    computeResult: response.data.data.computeResult,
    decisionCaseId: response.data.data.decisionCaseId,
  };
}

export async function selectRunCandidate(
  runId: string,
  optionId: string
): Promise<AiStudioRun> {
  const response = await api.post<ApiEnvelope<RunRow>>(
    `${STUDIO_BASE}/runs/${runId}/select-candidate`,
    { option_id: optionId }
  );
  return mapRun(response.data.data);
}

/** Built-in sample working set (mirrors backend SAMPLE_WORKING_SET). */
export const SAMPLE_SCHED_WORKING_SET: Record<string, unknown> = {
  horizonMinutes: 480,
  disruption: 'Resource R2 unavailable for the first 120 minutes.',
  resources: [
    { id: 'R1', type: 'machine', name: 'Line A', capacityMinutes: 480 },
    { id: 'R2', type: 'machine', name: 'Line B', capacityMinutes: 360 },
    { id: 'R3', type: 'machine', name: 'Line C', capacityMinutes: 480 },
  ],
  demands: [
    { id: 'D1', name: 'Order 1001', quantity: 100, dueMinutes: 240, priority: 1, unitValue: 50 },
    { id: 'D2', name: 'Order 1002', quantity: 80, dueMinutes: 360, priority: 2, unitValue: 40 },
    { id: 'D3', name: 'Order 1003', quantity: 60, dueMinutes: 480, priority: 3, unitValue: 30 },
    { id: 'D4', name: 'Rush 1004', quantity: 40, dueMinutes: 180, priority: 1, unitValue: 90 },
  ],
  tasks: [
    {
      id: 'T1',
      demandId: 'D1',
      name: 'Run 1001',
      durationMinutes: 120,
      eligibleResourceIds: ['R1', 'R3'],
    },
    {
      id: 'T2',
      demandId: 'D2',
      name: 'Run 1002',
      durationMinutes: 90,
      eligibleResourceIds: ['R1', 'R2'],
    },
    {
      id: 'T3',
      demandId: 'D3',
      name: 'Run 1003',
      durationMinutes: 100,
      eligibleResourceIds: ['R2', 'R3'],
    },
    {
      id: 'T4',
      demandId: 'D4',
      name: 'Run rush 1004',
      durationMinutes: 80,
      eligibleResourceIds: ['R1', 'R2', 'R3'],
    },
  ],
};

interface DecisionCaseRow {
  id: string;
  tenant_id: string;
  run_id: string | null;
  conversation_id: string | null;
  question: string;
  status: StudioDecisionCaseStatus;
  options?: unknown[] | null;
  assumptions?: Record<string, unknown> | null;
  orchestration_mode: string;
  opened_at: string;
  closed_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

function mapDecisionCase(row: DecisionCaseRow): AiStudioDecisionCase {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    runId: row.run_id,
    conversationId: row.conversation_id,
    question: row.question,
    status: row.status,
    options: row.options ?? [],
    assumptions: row.assumptions ?? {},
    orchestrationMode: row.orchestration_mode,
    openedAt: row.opened_at,
    closedAt: row.closed_at,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export async function listDecisionCases(runId?: string): Promise<AiStudioDecisionCase[]> {
  const response = await api.get<ApiEnvelope<DecisionCaseRow[]>>(
    `${STUDIO_BASE}/decision-cases`,
    runId ? { params: { run_id: runId } } : undefined
  );
  return response.data.data.map(mapDecisionCase);
}

export async function createDecisionCase(
  request: CreateDecisionCaseRequest
): Promise<AiStudioDecisionCase> {
  const response = await api.post<ApiEnvelope<DecisionCaseRow>>(
    `${STUDIO_BASE}/decision-cases`,
    {
      question: request.question,
      run_id: request.runId ?? null,
      conversation_id: request.conversationId ?? null,
      options: request.options ?? [],
      assumptions: request.assumptions ?? {},
    }
  );
  return mapDecisionCase(response.data.data);
}

export async function getDecisionCase(caseId: string): Promise<AiStudioDecisionCase> {
  const response = await api.get<ApiEnvelope<DecisionCaseRow>>(
    `${STUDIO_BASE}/decision-cases/${caseId}`
  );
  return mapDecisionCase(response.data.data);
}

export async function updateDecisionCase(
  caseId: string,
  request: UpdateDecisionCaseRequest
): Promise<AiStudioDecisionCase> {
  const body: Record<string, unknown> = {};
  if (request.question !== undefined) body.question = request.question;
  if (request.options !== undefined) body.options = request.options;
  if (request.assumptions !== undefined) body.assumptions = request.assumptions;
  const response = await api.patch<ApiEnvelope<DecisionCaseRow>>(
    `${STUDIO_BASE}/decision-cases/${caseId}`,
    body
  );
  return mapDecisionCase(response.data.data);
}

export async function decideDecisionCase(
  caseId: string,
  decision: StudioDecisionAction
): Promise<AiStudioDecisionCase> {
  const response = await api.post<ApiEnvelope<DecisionCaseRow>>(
    `${STUDIO_BASE}/decision-cases/${caseId}/decide`,
    { decision }
  );
  return mapDecisionCase(response.data.data);
}

interface ContributionRow {
  id: string;
  decision_case_id: string;
  agent_binding_id: string | null;
  contributor_role: string;
  facts_used?: Record<string, unknown> | null;
  options_or_constraints?: Record<string, unknown> | null;
  score_impact?: Record<string, unknown> | null;
  confidence: number | null;
  provenance?: Record<string, unknown> | null;
  created_at: string;
}

function mapContribution(row: ContributionRow): AiStudioDecisionContribution {
  return {
    id: row.id,
    decisionCaseId: row.decision_case_id,
    agentBindingId: row.agent_binding_id,
    contributorRole: row.contributor_role,
    factsUsed: row.facts_used ?? {},
    optionsOrConstraints: row.options_or_constraints ?? {},
    scoreImpact: row.score_impact ?? {},
    confidence: row.confidence,
    provenance: row.provenance ?? {},
    createdAt: row.created_at,
  };
}

export async function listDecisionContributions(
  caseId: string
): Promise<AiStudioDecisionContribution[]> {
  const response = await api.get<ApiEnvelope<ContributionRow[]>>(
    `${STUDIO_BASE}/decision-cases/${caseId}/contributions`
  );
  return response.data.data.map(mapContribution);
}

export async function addDecisionContribution(
  caseId: string,
  request: CreateDecisionContributionRequest
): Promise<AiStudioDecisionContribution> {
  const response = await api.post<ApiEnvelope<ContributionRow>>(
    `${STUDIO_BASE}/decision-cases/${caseId}/contributions`,
    {
      facts_used: request.factsUsed ?? {},
      options_or_constraints: request.optionsOrConstraints ?? {},
      score_impact: request.scoreImpact ?? {},
      confidence: request.confidence ?? null,
      provenance: request.provenance ?? {},
      contributor_role: request.contributorRole ?? 'primary',
      agent_binding_id: request.agentBindingId ?? null,
    }
  );
  return mapContribution(response.data.data);
}

// —— Ontology (S9) ——

interface OntologyConceptRow {
  id: string;
  tenant_id: string | null;
  code: string;
  name: string;
  description: string | null;
  concept_kind: OntologyConceptKind;
  version: number;
  is_platform: boolean;
  lifecycle_status: OntologyLifecycleStatus;
  created_at: string;
  updated_at: string;
}

interface OntologyRelationshipRow {
  id: string;
  tenant_id: string | null;
  code: string;
  name: string;
  description: string | null;
  from_concept_id: string;
  to_concept_id: string;
  cardinality: string;
  is_platform: boolean;
  lifecycle_status: OntologyLifecycleStatus;
  created_at: string;
  updated_at: string;
}

interface OntologyAttributeRow {
  id: string;
  concept_id: string;
  code: string;
  name: string;
  data_type: string;
  is_required: boolean;
  description: string | null;
  created_at: string;
  updated_at: string;
}

function mapConcept(row: OntologyConceptRow): AiOntologyConcept {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    code: row.code,
    name: row.name,
    description: row.description,
    conceptKind: row.concept_kind,
    version: row.version,
    isPlatform: row.is_platform,
    lifecycleStatus: row.lifecycle_status,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapRelationship(row: OntologyRelationshipRow): AiOntologyRelationship {
  return {
    id: row.id,
    tenantId: row.tenant_id,
    code: row.code,
    name: row.name,
    description: row.description,
    fromConceptId: row.from_concept_id,
    toConceptId: row.to_concept_id,
    cardinality: row.cardinality,
    isPlatform: row.is_platform,
    lifecycleStatus: row.lifecycle_status,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapAttribute(row: OntologyAttributeRow): AiOntologyAttribute {
  return {
    id: row.id,
    conceptId: row.concept_id,
    code: row.code,
    name: row.name,
    dataType: row.data_type,
    isRequired: row.is_required,
    description: row.description,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export async function listOntologyConcepts(
  status?: OntologyLifecycleStatus
): Promise<AiOntologyConcept[]> {
  const response = await api.get<ApiEnvelope<OntologyConceptRow[]>>(
    `${STUDIO_BASE}/ontology/concepts`,
    { params: status ? { status } : undefined }
  );
  return response.data.data.map(mapConcept);
}

export async function listOntologyRelationships(): Promise<AiOntologyRelationship[]> {
  const response = await api.get<ApiEnvelope<OntologyRelationshipRow[]>>(
    `${STUDIO_BASE}/ontology/relationships`
  );
  return response.data.data.map(mapRelationship);
}

// —— Agent Factory AI authoring interview (REQ-AIS-113) ——

interface AuthoringMessageRow {
  id: string;
  role: string;
  content: string;
  created_at: string;
  suggestedAgentPatch?: AuthoringAgentPatch;
  suggestedVersionPatch?: AuthoringVersionPatch;
}

function mapAuthoringMessage(row: AuthoringMessageRow): AuthoringMessage {
  return {
    id: row.id,
    role: row.role as AuthoringMessage['role'],
    content: row.content,
    createdAt: row.created_at,
    suggestedAgentPatch: row.suggestedAgentPatch,
    suggestedVersionPatch: row.suggestedVersionPatch,
  };
}

export async function getAgentAuthoringSession(
  agentId: string,
  versionId?: string
): Promise<AuthoringSession> {
  const response = await api.get<
    ApiEnvelope<{
      conversationId: string;
      versionId: string;
      messages: AuthoringMessageRow[];
      progress: DefinitionProgress;
    }>
  >(`${STUDIO_BASE}/agents/${agentId}/authoring`, {
    params: versionId ? { version_id: versionId } : undefined,
  });
  const data = response.data.data;
  return {
    conversationId: data.conversationId,
    versionId: data.versionId,
    messages: data.messages.map(mapAuthoringMessage),
    progress: data.progress,
  };
}

export async function sendAgentAuthoringMessage(
  agentId: string,
  content: string,
  versionId?: string
): Promise<AuthoringSendResult> {
  const response = await api.post<
    ApiEnvelope<{
      userMessage: AuthoringMessageRow;
      assistantMessage: AuthoringMessageRow;
      progress: DefinitionProgress;
      tokensUsed: number;
    }>
  >(
    `${STUDIO_BASE}/agents/${agentId}/authoring/messages`,
    { content, version_id: versionId ?? null },
    { timeout: 120000 }
  );
  const data = response.data.data;
  return {
    userMessage: mapAuthoringMessage(data.userMessage),
    assistantMessage: mapAuthoringMessage(data.assistantMessage),
    progress: data.progress,
    tokensUsed: data.tokensUsed,
  };
}

// —— Industry Pack AI authoring ——

interface PackAuthoringMessageRow {
  id: string;
  role: string;
  content: string;
  createdAt: string;
  suggestedPackPatch?: PackAuthoringPatch;
}

function mapPackAuthoringMessage(row: PackAuthoringMessageRow): PackAuthoringMessageRow {
  return row;
}

export async function getPackAuthoringSession(
  packId: string
): Promise<{ conversationId: string; packId: string; messages: PackAuthoringMessageRow[] }> {
  const response = await api.get<
    ApiEnvelope<{ conversationId: string; packId: string; messages: PackAuthoringMessageRow[] }>
  >(`${STUDIO_BASE}/packs/${packId}/authoring`);
  return response.data.data;
}

export async function sendPackAuthoringMessage(
  packId: string,
  content: string
): Promise<{
  userMessage: PackAuthoringMessageRow;
  assistantMessage: PackAuthoringMessageRow;
  tokensUsed: number;
}> {
  const response = await api.post<
    ApiEnvelope<{
      userMessage: PackAuthoringMessageRow;
      assistantMessage: PackAuthoringMessageRow;
      tokensUsed: number;
    }>
  >(
    `${STUDIO_BASE}/packs/${packId}/authoring/messages`,
    { content },
    { timeout: 120000 }
  );
  const data = response.data.data;
  return {
    userMessage: mapPackAuthoringMessage(data.userMessage),
    assistantMessage: mapPackAuthoringMessage(data.assistantMessage),
    tokensUsed: data.tokensUsed,
  };
}

// —— Instance provision AI assist (unified deploy + setup) ——

interface ProvisionAssistMessageRow {
  id: string;
  role: string;
  content: string;
  createdAt: string;
  suggestedProvisionPatch?: InstanceProvisionPatch;
}

export async function getProvisionAssistSession(input: {
  agentId?: string;
  instanceId?: string;
}): Promise<{
  conversationId: string;
  agentId: string | null;
  instanceId: string | null;
  messages: ProvisionAssistMessageRow[];
  setupStatus: import('@/services/aiStudioInstanceSetupService').InstanceSetupStatusDto | null;
}> {
  const params: Record<string, string> = {};
  if (input.agentId) params.agent_id = input.agentId;
  if (input.instanceId) params.instance_id = input.instanceId;
  const response = await api.get<
    ApiEnvelope<{
      conversationId: string;
      agentId: string | null;
      instanceId: string | null;
      messages: ProvisionAssistMessageRow[];
      setupStatus: import('@/services/aiStudioInstanceSetupService').InstanceSetupStatusDto | null;
    }>
  >(`${STUDIO_BASE}/provision/assist`, { params });
  return response.data.data;
}

export async function sendProvisionAssistMessage(
  input: {
    agentId?: string;
    instanceId?: string;
    content: string;
    draft?: InstanceDeployPatch & {
      agentId?: string;
      pinnedVersionId?: string;
      instanceId?: string;
    };
  }
): Promise<{
  userMessage: ProvisionAssistMessageRow;
  assistantMessage: ProvisionAssistMessageRow;
  setupStatus: import('@/services/aiStudioInstanceSetupService').InstanceSetupStatusDto | null;
  tokensUsed: number;
}> {
  const response = await api.post<
    ApiEnvelope<{
      userMessage: ProvisionAssistMessageRow;
      assistantMessage: ProvisionAssistMessageRow;
      setupStatus: import('@/services/aiStudioInstanceSetupService').InstanceSetupStatusDto | null;
      tokensUsed: number;
    }>
  >(
    `${STUDIO_BASE}/provision/assist/messages`,
    {
      content: input.content,
      agent_id: input.agentId,
      instance_id: input.instanceId,
      draft: input.draft,
    },
    { timeout: 120000 }
  );
  return response.data.data;
}

// —— Instance deploy AI assist ——

interface DeployAssistMessageRow {
  id: string;
  role: string;
  content: string;
  createdAt: string;
  suggestedDeployPatch?: InstanceDeployPatch;
}

export async function getDeployAssistSession(agentId: string): Promise<{
  conversationId: string;
  agentId: string;
  messages: DeployAssistMessageRow[];
}> {
  const response = await api.get<
    ApiEnvelope<{ conversationId: string; agentId: string; messages: DeployAssistMessageRow[] }>
  >(`${STUDIO_BASE}/deploy/assist`, { params: { agent_id: agentId } });
  return response.data.data;
}

export async function sendDeployAssistMessage(
  agentId: string,
  content: string,
  draft?: InstanceDeployPatch & { agentId?: string; pinnedVersionId?: string }
): Promise<{
  userMessage: DeployAssistMessageRow;
  assistantMessage: DeployAssistMessageRow;
  tokensUsed: number;
}> {
  const response = await api.post<
    ApiEnvelope<{
      userMessage: DeployAssistMessageRow;
      assistantMessage: DeployAssistMessageRow;
      tokensUsed: number;
    }>
  >(
    `${STUDIO_BASE}/deploy/assist/messages`,
    { content, agent_id: agentId, draft },
    { timeout: 120000 }
  );
  return response.data.data;
}

export async function listOntologyAttributes(
  conceptId: string
): Promise<AiOntologyAttribute[]> {
  const response = await api.get<ApiEnvelope<OntologyAttributeRow[]>>(
    `${STUDIO_BASE}/ontology/concepts/${conceptId}/attributes`
  );
  return response.data.data.map(mapAttribute);
}
